/* eslint-disable global-require */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-var-requires */

type LogMeta = Record<string, unknown>
type LogMethod = (message?: unknown, ...args: unknown[]) => void

type LoggerLike = {
  info: LogMethod
  error: LogMethod
  warn: LogMethod
  debug: LogMethod
}

type StructuredLogger = {
  info: (message: string, meta?: LogMeta) => void
  error: (message: string, meta?: LogMeta) => void
  warn: (message: string, meta?: LogMeta) => void
  debug: (message: string, meta?: LogMeta) => void
}

type LogLevel = keyof StructuredLogger

function createWinstonLogger(): StructuredLogger {
  const winston = require('winston')
  return winston.createLogger({
    level: process.env.LOG_LEVEL || 'info',
    format: winston.format.combine(winston.format.timestamp(), winston.format.json()),
    defaultMeta: {
      service: 'carbon',
      env: process.env.ACKO_ENV,
    },
    transports: [new winston.transports.Console()],
  })
}

function isPlainObject(value: unknown): value is LogMeta {
  if (value === null || typeof value !== 'object') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof Error) return value.message

  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }

  return String(value)
}

function normalizeArgs(args: unknown[]): { message: string; meta?: LogMeta } {
  if (args.length === 0) return { message: '' }

  const [first, ...rest] = args
  const meta: LogMeta = {}
  const messageParts: string[] = []
  const errors: Error[] = []

  if (isPlainObject(first)) {
    Object.assign(meta, first)
    messageParts.push(formatValue(first))
  } else if (first instanceof Error) {
    errors.push(first)
    messageParts.push(first.message)
  } else {
    messageParts.push(formatValue(first))
  }

  rest.forEach(value => {
    if (value instanceof Error) {
      errors.push(value)
    } else if (isPlainObject(value)) {
      Object.assign(meta, value)
    } else {
      messageParts.push(formatValue(value))
    }
  })

  if (errors.length > 0) {
    meta.error = errors.map(error => error.message).join('; ')
    meta.stacktrace = errors.flatMap(error => error.stack?.split('\n') ?? [])
  }

  return {
    message: messageParts.join(' '),
    ...(Object.keys(meta).length > 0 ? { meta } : {}),
  }
}

// Winston is Node-only (uses `fs`). Shared modules can also run in the browser,
// where the same API intentionally falls back to the native console.
const winstonLogger = typeof window === 'undefined' ? createWinstonLogger() : undefined

function write(level: LogLevel, args: unknown[]) {
  if (!winstonLogger) {
    console[level](...args)
    return
  }

  const { message, meta } = normalizeArgs(args)
  winstonLogger[level](message, meta)
}

const logger: LoggerLike = {
  info: (...args) => write('info', args),
  error: (...args) => write('error', args),
  warn: (...args) => write('warn', args),
  debug: (...args) => write('debug', args),
}

export function errorWithStack(message: string, err: unknown, meta: LogMeta = {}) {
  const error = err instanceof Error ? err : new Error(String(err))
  logger.error(message, {
    ...meta,
    error: error.message,
    stacktrace: error.stack?.split('\n'),
  })
}

export default logger
